/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CustomTooltip } from '@opengeoweb/shared';
import type { CoreAppStore } from '@opengeoweb/store';
import { uiActions, uiSelectors } from '@opengeoweb/store';
import { Layers } from '@opengeoweb/theme';
import { DIALOG_TYPE_MANAGER } from './TimeSeriesManagerConnect';
import { useTimeseriesTranslation } from '../../utils/i18n';
import { TimeSeriesTopRowButton } from '../TimeSeries/TimeSeriesTopRowButton';

export const TOOLTIP_TITLE = 'Timeseries Manager';

interface TimeSeriesManagerMapButtonConnectProps {
  viewId: string;
}

const TimeSeriesManagerMapButtonConnect: React.FC<
  TimeSeriesManagerMapButtonConnectProps
> = ({ viewId }: TimeSeriesManagerMapButtonConnectProps) => {
  const dispatch = useDispatch();
  const { t } = useTimeseriesTranslation();

  const currentSourcePanelId = useSelector((store: CoreAppStore) =>
    uiSelectors.getSourcePanelId(store, DIALOG_TYPE_MANAGER),
  );

  const isOpenInStore = useSelector((store: CoreAppStore) =>
    uiSelectors.getisDialogOpen(store, DIALOG_TYPE_MANAGER),
  );

  const openTimeSeriesManagerDialog = React.useCallback((): void => {
    dispatch(
      uiActions.setToggleOpenDialog({
        type: DIALOG_TYPE_MANAGER,
        setOpen: currentSourcePanelId !== viewId ? true : !isOpenInStore,
        sourcePanelId: viewId,
      }),
    );
  }, [currentSourcePanelId, dispatch, isOpenInStore, viewId]);

  const isOpen = currentSourcePanelId === viewId && isOpenInStore;

  return (
    <CustomTooltip title={TOOLTIP_TITLE}>
      <span>
        <TimeSeriesTopRowButton
          aria-label={t('timeseries-manager')}
          data-testid="timeSeriesManagerButton"
          icon={<Layers />}
          longText={t('timeseries-ts-manager')}
          onClick={openTimeSeriesManagerDialog}
          selected={isOpen}
        />
      </span>
    </CustomTooltip>
  );
};

export default TimeSeriesManagerMapButtonConnect;
